import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ProductImagesStep from "./ProductImagesStep";

/**
 * Standalone "Add images" dialog on Manage Products: the same bulk image
 * upload as the import's last step, for products that already exist.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {object[]} products  rows as Manage Products holds them
 * @param {() => void} onImagesChanged  called on close when at least one image was uploaded
 */
export default function ProductImagesDialog({
  open,
  onClose,
  products,
  onImagesChanged,
}) {
  const changedRef = useRef(false);
  // Uploads queued or in flight. The dialog stays open until they finish so
  // the product list reloads with every new image.
  const [busy, setBusy] = useState(0);

  useEffect(() => {
    if (open) {
      changedRef.current = false;
      setBusy(0);
    }
  }, [open]);

  const stepProducts = useMemo(
    () =>
      (products ?? []).map((row) => ({
        product_id: row.product_id,
        barcode: row.barcode,
        product_name: row.name,
        brand: row.brand,
        is_retail: Boolean(row.isRetail),
        product_image: row.product_image || null,
      })),
    [products]
  );

  const handleUploaded = useCallback(() => {
    changedRef.current = true;
  }, []);

  const handleClose = () => {
    if (busy > 0) return;
    if (changedRef.current && onImagesChanged) onImagesChanged();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={busy > 0 ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          bgcolor: "secondary.main",
          color: "common.white",
          borderBottom: "1px solid",
          borderColor: "primary.main",
          px: 3,
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="inherit">
            Add images
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
          >
            Attach pictures to your products in bulk, or one at a time.
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={busy > 0}
          size="small"
          sx={{
            color: "primary.main",
            mt: 0.25,
            "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {open && (
          <ProductImagesStep
            products={stepProducts}
            onImageUploaded={handleUploaded}
            onBusyChange={setBusy}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleClose}
          disabled={busy > 0}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {busy > 0 ? `Uploading ${busy}…` : "Done"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
